import { UseQueryOptions } from '@tanstack/react-query';

import { DisplayConditionListItem } from 'hooks/mutations/useArchiveDisplayCondition';
import { CuredApiPaths } from 'models/enums';

import useQuery from '../useQuery';

import QueryKeys from './keys';

export type RowDisplayCondition = {
    id: number;
    label: string;
    description: string;
    before: string;
    after: string;
    type: string;
};

/**
 * Fetches the full, unpaginated, active-only condition list used by the Bee editor's row
 * condition picker. Do not paginate or filter this — it must stay a drop-in-safe superset of
 * what the picker has always returned. Use `useManageDisplayConditions` for the Manage table.
 */
const useRowDisplayConditions = (reactQueryOptions?: UseQueryOptions<RowDisplayCondition[], Error>) =>
    useQuery<RowDisplayCondition[]>(
        QueryKeys.ROW_DISPLAY_CONDITIONS,
        `${CuredApiPaths.ROW_DISPLAY_CONDITIONS}`,
        reactQueryOptions
    );

export default useRowDisplayConditions;

export type ManageDisplayConditionsResponse = {
    total: number;
    offset: number;
    limit: number;
    data: DisplayConditionListItem[];
};

export type UseManageDisplayConditionsParams = {
    limit?: number;
    offset?: number;
    search?: string;
    category?: string;
    /** Defaults to `false` (active) server-side when omitted. */
    archived?: boolean;
};

/**
 * Paginated, filterable listing for the Manage Display Conditions table. Separate from
 * `useRowDisplayConditions`, which must keep returning an unpaginated, active-only array.
 */
export const useManageDisplayConditions = (
    params: UseManageDisplayConditionsParams = {},
    reactQueryOptions?: UseQueryOptions<ManageDisplayConditionsResponse, Error>
) => {
    const { limit, offset, search, category, archived } = params;

    const queryKey = QueryKeys.MANAGE_DISPLAY_CONDITIONS(limit, offset, search, category, archived);

    const axiosParams: Record<string, string | number> = {};
    if (limit !== undefined) {
        axiosParams.limit = limit;
    }
    if (offset !== undefined) {
        axiosParams.offset = offset;
    }
    if (search?.trim()) {
        axiosParams.search = search.trim();
    }
    if (category) {
        axiosParams.category = category;
    }
    if (archived !== undefined) {
        axiosParams.archived = String(archived);
    }

    const axiosOptions = Object.keys(axiosParams).length > 0 ? { params: axiosParams } : {};

    return useQuery<ManageDisplayConditionsResponse>(
        queryKey,
        CuredApiPaths.MANAGE_DISPLAY_CONDITIONS,
        reactQueryOptions,
        axiosOptions
    );
};
